import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';

import { isPageItem } from 'components/Sidebar/menuTree';
import { List } from 'immutable';
import Crumb from './Crumb';
import searchJsx from './searchJsx';

const getUrl = (url) => (isPageItem(url) ? url.url : url);

const verifyPermissions = (scope, item) => {
  if (!item.permissions) {
    return true;
  }
  return item.permissions.reduce(
    (acc, permission) => (scope.indexOf(permission) >= 0 ? acc : false),
    true,
  );
};

const searchText = (text, q) => {
  const re = new RegExp(q, 'gi');
  if (isPlainObject(text) && text.$$typeof !== 'undefined') {
    return searchJsx(null, q, text);
  }
  return text.toLowerCase().match(re);
};

const searchColumns = (text, type = 'name', q = undefined) => {
  const re = new RegExp(q, 'gi');
  return Object.keys(text).reduce((acc, key) => {
    const val = text[key][type];
    if (!val) {
      return acc;
    }
    const match = val.toLowerCase().match(re);
    if (!match) {
      return acc;
    }
    return Array.isArray(acc) ? acc.concat(match) : match;
  }, null);
};

const labelSignificance = (crumb, q, count) => {
  const parentCount = crumb.hasParent()
    ? labelSignificance(crumb.getParent(), q, count)
    : 0;
  const name = crumb.getName();
  const matchLabel = searchText(name || '', q);
  return parentCount + (matchLabel?.length ?? 0);
};

const searchLabel = (crumb, q) => {
  const name = crumb.getName();
  if (!name) {
    return false;
  }
  const matchLabel = searchText(name || '', q);
  if (matchLabel) {
    return true;
  }
  if (crumb.hasParent()) {
    return searchLabel(crumb.getParent(), q);
  }
  return false;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
const itemSearch = (item, q, acc, crumb) => {
  const matchLabel = searchLabel(crumb, q);
  const matchBoxTitle = item.boxTitle
    ? searchText(item.boxTitle, q)
    : item.msg?.boxTitle && searchText(item.msg.boxTitle, q);
  const matchDescription =
    item.msg?.boxHelpText && searchText(item.msg.boxHelpText, q);
  const matchColumnsName =
    item.msg &&
    (item.msg.columns || item.msg.table) &&
    searchColumns(item.msg.columns || item.msg.table, 'name', q);
  const matchColumnsHelp =
    item.msg &&
    (item.msg.columns || item.msg.table) &&
    searchColumns(item.msg.columns || item.msg.table, 'help', q);
  if (
    matchLabel ||
    matchBoxTitle ||
    matchDescription ||
    matchColumnsName ||
    matchColumnsHelp
  ) {
    let significance = (matchBoxTitle?.length ?? 0) * 1000;
    significance += labelSignificance(crumb, q, 0) * 200;
    significance += (matchDescription?.length ?? 0) * 100;
    significance += (matchColumnsName?.length ?? 0) * 10;
    significance += (matchColumnsHelp?.length ?? 0) * 1;
    return acc.push({
      breadCrumb: crumb,
      label:
        item.label ||
        get(item, ['msg', 'title'], null) ||
        get(item, ['msg', 'pageTitle'], ''),
      boxTitle: get(item, 'boxTitle', get(item, ['msg', 'boxTitle'], '')),
      title: get(item, ['msg', 'title']) || get(item, ['msg', 'pageTitle'], ''),
      description: get(item, ['msg', 'boxHelpText']),
      columns: get(item, ['msg', 'columns']) || get(item, ['msg', 'table']),
      url:
        getUrl(item.url) ||
        (Array.isArray(item.urlList) ? getUrl(item.urlList[0]) : ''),
      significance,
    });
  }
  return acc;
};

const searchSubtree = (tree, q, results, parentCrumb, scope) => {
  const keys = Object.keys(tree);
  return keys.reduce((acc, key) => {
    const item = tree[key];
    if (!verifyPermissions(scope, item)) {
      return acc;
    }
    const crumbName =
      item.label ||
      get(item, ['msg', 'title'], null) ||
      get(item, ['msg', 'pageTitle'], '');
    const crumb = parentCrumb
      ? parentCrumb.addChild(crumbName)
      : new Crumb(crumbName);
    const searchInItem = item.urlList || item.url;
    const resultsWithItem =
      searchInItem && !item.disableItemSearch
        ? itemSearch(item, q, acc, crumb)
        : acc;
    const resultsWithChildren = item.children
      ? searchSubtree(item.children, q, resultsWithItem, crumb, scope)
      : resultsWithItem;
    const searchChildren = item.urlList?.filter((child) =>
      isPlainObject(child),
    );
    return searchChildren?.length
      ? searchSubtree(searchChildren, q, resultsWithChildren, crumb, scope)
      : resultsWithChildren;
  }, results);
};

const findResults = ({ q, menuTree, scope }) => {
  return searchSubtree(menuTree, q, new List(), null, scope);
};

export default findResults;
